/**
 * FeedbackPage - Direct access to feedback modal
 * Issue #1075
 *
 * Shareable URL (/feedback) for sending to testers.
 * Opens the feedback modal directly on a minimal page.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthModal } from '../contexts/AuthModalContext';
import FeedbackModal from '../components/FeedbackModal';
import { MessageSquare } from 'lucide-react';
import { BRAND_FULL } from '../constants/brand';

export default function FeedbackPage() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const { openLoginModal } = useAuthModal();
  const [showModal, setShowModal] = useState(false);

  // React docs "adjust state on prop change" — open modal automatically when
  // auth resolves and user is authenticated.
  const [prevAuthGate, setPrevAuthGate] = useState({ isLoading, user });
  if (prevAuthGate.isLoading !== isLoading || prevAuthGate.user !== user) {
    setPrevAuthGate({ isLoading, user });
    if (!isLoading && user) {
      setShowModal(true);
    }
  }

  // Handle modal close - navigate back or to home
  const handleClose = () => {
    setShowModal(false);
    // Give animation time to complete
    setTimeout(() => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/');
      }
    }, 150);
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center">
        <div className="text-text-muted">Loading...</div>
      </div>
    );
  }

  // Not authenticated - show login prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center p-4">
        <div className="bg-oro-surface border border-oro-border rounded-xl p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 rounded-full bg-oro-surface-alt flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-accent" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary mb-2">
            Share Your Feedback
          </h1>
          <p className="text-text-secondary mb-6">
            Please sign in to submit feedback about {BRAND_FULL}.
          </p>
          <button
            onClick={() => openLoginModal('/feedback')}
            className="w-full py-3 bg-accent text-oro-bg font-medium rounded-lg hover:bg-accent/90 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oro-bg">
      <FeedbackModal isOpen={showModal} onClose={handleClose} />
    </div>
  );
}
